/**
 * Dev Playground shared primitive components.
 * Underscore prefix = not a route file.
 */

import { useState, type ReactNode } from "react";

// ---------------------------------------------------------------------------
// Error serializer — Error properties aren't JSON-enumerable
// ---------------------------------------------------------------------------

function serializeError(err: unknown): Record<string, unknown> {
  if (err instanceof Error) {
    return {
      name: err.name,
      message: err.message,
      // Convex surfaces extra data on ConvexError
      ...("data" in err && err.data !== undefined
        ? { data: (err as { data: unknown }).data }
        : {}),
      stack: err.stack,
    };
  }
  if (err !== null && typeof err === "object") {
    return err as Record<string, unknown>;
  }
  return { raw: String(err) };
}

// ---------------------------------------------------------------------------
// JsonViewer
// ---------------------------------------------------------------------------

interface JsonViewerProps {
  data: unknown;
  label?: string;
}

export function JsonViewer({ data, label }: JsonViewerProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [copied, setCopied] = useState(false);

  const json = JSON.stringify(data, null, 2);

  const isSuccess =
    data !== null &&
    typeof data === "object" &&
    "success" in (data as Record<string, unknown>) &&
    (data as Record<string, unknown>).success === true;

  const isFailure =
    data instanceof Error ||
    (data !== null &&
      typeof data === "object" &&
      "success" in (data as Record<string, unknown>) &&
      (data as Record<string, unknown>).success === false) ||
    // serialized error (from serializeError helper)
    (data !== null &&
      typeof data === "object" &&
      "message" in (data as Record<string, unknown>) &&
      "stack" in (data as Record<string, unknown>));

  const borderClass = isSuccess
    ? "border-green-500 bg-green-950/20"
    : isFailure
      ? "border-red-500 bg-red-950/20"
      : "border-border bg-muted/20";

  async function handleCopy() {
    await navigator.clipboard.writeText(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className={`rounded border ${borderClass} text-xs`}>
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
        <span className="font-medium text-muted-foreground">
          {label ?? "JSON"}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="text-muted-foreground hover:text-foreground"
          >
            {collapsed ? "▶ expand" : "▼ collapse"}
          </button>
          <button
            type="button"
            onClick={handleCopy}
            className="text-muted-foreground hover:text-foreground"
          >
            {copied ? "✓ copied" : "copy"}
          </button>
        </div>
      </div>
      {!collapsed && (
        <pre className="max-h-80 overflow-auto p-3 text-xs leading-relaxed">
          {json}
        </pre>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActionButton
// ---------------------------------------------------------------------------

interface ActionButtonProps {
  label: string;
  onClick: () => Promise<unknown>;
  onResult?: (result: {
    data: unknown;
    error: unknown;
    timestamp: number;
  }) => void;
  variant?: "default" | "destructive";
  disabled?: boolean;
}

export function ActionButton({
  label,
  onClick,
  onResult,
  variant = "default",
  disabled = false,
}: ActionButtonProps) {
  const [pending, setPending] = useState(false);

  const baseClass =
    "inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50";
  const variantClass =
    variant === "destructive"
      ? "bg-red-600 text-white hover:bg-red-700"
      : "bg-accent text-accent-foreground hover:bg-accent/90";

  async function handle() {
    setPending(true);
    try {
      const data = await onClick();
      onResult?.({ data, error: null, timestamp: Date.now() });
    } catch (err) {
      onResult?.({
        data: null,
        error: serializeError(err),
        timestamp: Date.now(),
      });
    } finally {
      setPending(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handle}
      disabled={disabled || pending}
      className={`${baseClass} ${variantClass}`}
    >
      {pending && (
        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// ResultPanel
// ---------------------------------------------------------------------------

interface ResultPanelProps {
  result: {
    data: unknown;
    error: unknown;
    timestamp: number;
    label?: string;
  } | null;
}

export function ResultPanel({ result }: ResultPanelProps) {
  if (!result) return null;

  const ts = new Date(result.timestamp).toLocaleTimeString();

  // Two kinds of failure:
  //   1. Thrown exception — caught by ActionButton, lands in result.error
  //   2. makeHandler wraps errors as { success: false, error } — lands in result.data
  const threwException = result.error !== null;
  const returnedFailure =
    result.data !== null &&
    typeof result.data === "object" &&
    "success" in (result.data as Record<string, unknown>) &&
    (result.data as Record<string, unknown>).success === false;

  const hasError = threwException || returnedFailure;

  const errorMessage = threwException
    ? (((result.error as Record<string, unknown>)?.message as
        | string
        | undefined) ?? "Unknown error")
    : (((
        (result.data as Record<string, unknown>)?.error as Record<
          string,
          unknown
        >
      )?.message as string | undefined) ?? "Unknown error");

  const value = result.error ?? result.data;
  const label = result.label ? `Result of: ${result.label}` : "Result";

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground">
          {label}
        </span>
        <span className="text-xs text-muted-foreground">{ts}</span>
      </div>
      {hasError && (
        <div className="flex items-center gap-2 rounded border border-red-500 bg-red-950/30 px-3 py-2">
          <span className="text-sm">⚠</span>
          <span className="text-xs font-semibold text-red-400">
            {threwException ? "THREW" : "FAILED"} — {errorMessage}
          </span>
        </div>
      )}
      {!hasError && (
        <div className="flex items-center gap-2 rounded border border-green-600 bg-green-950/20 px-3 py-2">
          <span className="text-sm">✓</span>
          <span className="text-xs font-semibold text-green-400">OK</span>
        </div>
      )}
      <JsonViewer data={value} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// EntityTable
// ---------------------------------------------------------------------------

interface EntityTableProps<T> {
  rows: T[];
  columns: { header: string; render: (row: T) => ReactNode }[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  getId: (row: T) => string;
  isLoading?: boolean;
}

export function EntityTable<T>({
  rows,
  columns,
  selectedId,
  onSelect,
  getId,
  isLoading,
}: EntityTableProps<T>) {
  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center text-xs text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center text-xs text-muted-foreground">
        No rows
      </div>
    );
  }

  return (
    <div
      className="overflow-auto rounded border border-border"
      style={{ maxHeight: "calc(100vh - 200px)" }}
    >
      <table className="w-full text-xs">
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            {columns.map((col) => (
              <th
                key={col.header}
                className="border-b border-border px-3 py-2 text-left font-semibold text-muted-foreground"
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const id = getId(row);
            const selected = id === selectedId;
            return (
              <tr
                key={id}
                onClick={() => onSelect(id)}
                className={`cursor-pointer border-b border-border last:border-0 hover:bg-muted/50 ${
                  selected ? "bg-primary/10 hover:bg-primary/20" : ""
                }`}
              >
                {columns.map((col) => (
                  <td key={col.header} className="px-3 py-2">
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DevSection
// ---------------------------------------------------------------------------

interface DevSectionProps {
  title: string;
  children: ReactNode;
  danger?: boolean;
}

export function DevSection({
  title,
  children,
  danger = false,
}: DevSectionProps) {
  return (
    <div
      className={`rounded border p-4 ${
        danger ? "border-red-500/50 bg-red-950/10" : "border-border bg-muted/10"
      }`}
    >
      <h3
        className={`mb-3 text-xs font-semibold uppercase tracking-wider ${
          danger ? "text-red-400" : "text-muted-foreground"
        }`}
      >
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatusBadge helper
// ---------------------------------------------------------------------------

export function StatusBadge({ status }: { status: string | undefined }) {
  const color =
    status === "completed"
      ? "bg-green-500/20 text-green-400 border-green-500/30"
      : status === "failed"
        ? "bg-red-500/20 text-red-400 border-red-500/30"
        : status === "pending" || status === "generating"
          ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
          : "bg-muted text-muted-foreground border-border";

  return (
    <span
      className={`inline-flex rounded border px-1.5 py-0.5 text-xs font-medium ${color}`}
    >
      {status ?? "—"}
    </span>
  );
}
